import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Descriptions, Modal, Space, Tabs, Table, Tag, Tooltip, Typography } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  LeftOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paths from "routes/paths";
import olreadiaService from "services/olreadiaService";
import { useAuth } from "contexts/AuthContext";
import {
  csvToArray,
  formatAnswerOptions,
  formatLocations,
  formatLongDateTime,
  formatText,
  tagStatusText,
  toMessage,
} from "./utils";
import { toast } from "react-toastify";
import JobOpportunityDrawer from "./JobOpportunityDrawer";
import JobOpportunityQuestionDrawer from "./JobOpportunityQuestionDrawer";
import JobOpportunityQuestionDetailsDrawer from "./JobOpportunityQuestionDetailsDrawer";

const toSelectIds = (items) =>
  Array.isArray(items) ? items.map((item) => String(item?.id)).filter((val) => val && val !== "undefined" && val !== "null") : [];

const mapOpportunityForForm = (jobOpportunity) => {
  if (!jobOpportunity) return null;
  return {
    ...jobOpportunity,
    requiredSpecializationIds: toSelectIds(jobOpportunity.requiredSpecializations),
    requiredSkillIds: toSelectIds(jobOpportunity.requiredSkills),
    otherRequiredSpecializations: csvToArray(jobOpportunity.otherRequiredSpecializations).join(", "),
    otherRequiredWorkAreas: csvToArray(jobOpportunity.otherRequiredWorkAreas).join(", "),
    otherRequiredSkills: csvToArray(jobOpportunity.otherRequiredSkills).join(", "),
	    jobLocations: jobOpportunity.jobLocations || [],
	    requiredLocations: jobOpportunity.requiredLocations || [],
  };
};

export default function JobOpportunityDetailsPage() {
  const { jobOpportunityId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const actorId = user?.id || "";

  const [jobOpportunity, setJobOpportunity] = useState(location.state?.jobOpportunity || null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("details");

  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsError, setQuestionsError] = useState(null);
  const [questionDrawerOpen, setQuestionDrawerOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [questionDetailsOpen, setQuestionDetailsOpen] = useState(false);
  const [savingQuestion, setSavingQuestion] = useState(false);
  const [deletingQuestion, setDeletingQuestion] = useState(false);
  const [deletingJob, setDeletingJob] = useState(false);
  const [options, setOptions] = useState({ skills: [], specializations: [] });

  const loadJobOpportunity = useCallback(async () => {
    if (!jobOpportunityId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await olreadiaService.jobOpportunities.get(jobOpportunityId);
      setJobOpportunity(res);
    } catch (err) {
      setError(toMessage(err, "Failed to load job opportunity"));
    } finally {
      setLoading(false);
    }
  }, [jobOpportunityId]);

  const loadQuestions = useCallback(async () => {
    if (!jobOpportunityId) return;
    setQuestionsLoading(true);
    setQuestionsError(null);
    try {
      const res = await olreadiaService.jobOpportunities.questions.list(jobOpportunityId);
      setQuestions(Array.isArray(res) ? res : []);
    } catch (err) {
      setQuestionsError(toMessage(err, "Failed to load job opportunity questions"));
    } finally {
      setQuestionsLoading(false);
    }
  }, [jobOpportunityId]);

  const loadOptions = useCallback(async () => {
    const [skillsRes, specializationsRes] = await Promise.all([
      olreadiaService.professionalSkills.list(),
      olreadiaService.professionalSpecializations.list(),
    ]);
    setOptions({
      skills: Array.isArray(skillsRes) ? skillsRes : [],
      specializations: Array.isArray(specializationsRes) ? specializationsRes : [],
    });
  }, []);

  useEffect(() => {
    loadJobOpportunity();
    loadQuestions();
    loadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobOpportunityId]);

  const handleDeleteJob = () => {
    Modal.confirm({
      title: "Remove job opportunity?",
      content: "This will permanently delete the job opportunity.",
      okText: "Remove",
      okButtonProps: { danger: true, loading: deletingJob },
      onOk: async () => {
        setDeletingJob(true);
        try {
          await olreadiaService.jobOpportunities.delete(jobOpportunityId, actorId);
          toast.success("Job opportunity removed");
          navigate(paths.jobOpportunities);
        } catch (err) {
          toast.error(toMessage(err, "Failed to delete job opportunity"));
        } finally {
          setDeletingJob(false);
        }
      },
    });
  };

  const handleSubmitJob = async (values) => {
    setSaving(true);
    try {
      if (jobOpportunity?.id) {
        await olreadiaService.jobOpportunities.update(jobOpportunity.id, values, actorId);
        toast.success("Job opportunity updated");
      } else {
        await olreadiaService.jobOpportunities.create(values, actorId);
        toast.success("Job opportunity created");
      }
      setDrawerOpen(false);
      await loadJobOpportunity();
      await loadQuestions();
    } catch (err) {
      toast.error(toMessage(err, "Failed to save job opportunity"));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitQuestion = async (values) => {
    if (!jobOpportunityId) return;
    setSavingQuestion(true);
    try {
      if (editingQuestion?.id) {
        await olreadiaService.jobOpportunities.questions.update(
          jobOpportunityId,
          editingQuestion.id,
          values,
          actorId,
        );
        toast.success("Question updated");
      } else {
        await olreadiaService.jobOpportunities.questions.create(jobOpportunityId, values, actorId);
        toast.success("Question created");
      }
      setQuestionDrawerOpen(false);
      await loadQuestions();
    } catch (err) {
      toast.error(toMessage(err, "Failed to save question"));
    } finally {
      setSavingQuestion(false);
    }
  };

  const handleDeleteQuestion = useCallback((record) => {
    Modal.confirm({
      title: "Remove question?",
      content: "This will permanently delete the question from this job opportunity.",
      okText: "Remove",
      okButtonProps: { danger: true, loading: deletingQuestion },
      onOk: async () => {
        setDeletingQuestion(true);
        try {
          await olreadiaService.jobOpportunities.questions.delete(jobOpportunityId, record.id, actorId);
          await loadQuestions();
          toast.success("Question removed");
        } catch (err) {
          toast.error(toMessage(err, "Failed to delete question"));
        } finally {
          setDeletingQuestion(false);
        }
      },
    });
  }, [actorId, jobOpportunityId, loadQuestions, deletingQuestion]);

  const questionColumns = useMemo(
    () => [
      {
        title: "Question",
        dataIndex: "question",
        key: "question",
        ellipsis: true,
        render: (value) => {
          const text = value || "-";
          return (
            <Tooltip title={text === "-" ? "" : text} placement="topLeft">
              <span style={{ fontWeight: 600 }}>{formatText(text, 48)}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "Order",
        dataIndex: "orderNumber",
        key: "orderNumber",
        width: 100,
      },
      {
        title: "Answer Type",
        dataIndex: "answerType",
        key: "answerType",
        width: 150,
        render: (value) => <Tag color="green">{value || "-"}</Tag>,
      },
      {
        title: "Value Type",
        dataIndex: "answerValueType",
        key: "answerValueType",
        width: 150,
        render: (value) => <Tag color="blue">{value || "-"}</Tag>,
      },
      {
        title: "Answer Options",
        key: "answerOptions",
        ellipsis: true,
        render: (_, record) => {
          const options = formatAnswerOptions(record?.answerOptions);
          const text = options.length ? options.join(", ") : "-";
          return (
            <Tooltip title={text === "-" ? "" : text} placement="topLeft">
              <span>{formatText(text, 48)}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "Metric",
        dataIndex: "answerMetric",
        key: "answerMetric",
        width: 140,
        render: (value) => value || "-",
      },
      {
        title: "Actions",
        key: "actions",
        width: 90,
        render: (_, record) => (
          <div className="prof-jobs-actions" onClick={(e) => e.stopPropagation()}>
            <Space size={0}>
              <Button
                size="small"
                type="text"
                icon={<EyeOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedQuestion(record);
                  setQuestionDetailsOpen(true);
                }}
              />
              <Button
                size="small"
                type="text"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingQuestion(record);
                  setQuestionDrawerOpen(true);
                }}
              />
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteQuestion(record);
                }}
              />
            </Space>
          </div>
        ),
      },
    ],
    [handleDeleteQuestion],
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon="briefcase"
          title={jobOpportunity?.title || "Job Opportunity"}
          subtitle="Details and questions for this job posting."
          className="mb-2 mt-2 flex-1"
        />
        <Space>
          <Button icon={<LeftOutlined />} onClick={() => navigate(paths.jobOpportunities)}>
            Back
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => loadJobOpportunity()} loading={loading}>
            Refresh
          </Button>
          <Button
            icon={<EditOutlined />}
            type="primary"
            loading={loading}
            disabled={!jobOpportunity}
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            Edit
          </Button>
          <Button danger icon={<DeleteOutlined />} disabled={!jobOpportunity} loading={deletingJob} onClick={handleDeleteJob}>
            Delete
          </Button>
        </Space>
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load"
          description={error}
          style={{ marginBottom: 12 }}
          action={<Button size="small" onClick={() => loadJobOpportunity()}>Retry</Button>}
        />
      ) : null}

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "details",
            label: "Details",
            children: (
              <Space direction="vertical" size={16} style={{ width: "100%" }}>
                <Descriptions bordered size="small" column={2}>
                  <Descriptions.Item label="Status">
                    <Tag color="green">{tagStatusText(jobOpportunity?.status)}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Job Type">
                    {Array.isArray(jobOpportunity?.workSchedules) && jobOpportunity.workSchedules.length
                      ? jobOpportunity.workSchedules[0].replace(/_/g, " ")
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Education Level">
                    {jobOpportunity?.requiredEducationLevel
                      ? jobOpportunity.requiredEducationLevel.replace(/_/g, " ")
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Years of Experience">
                    {jobOpportunity?.requiredYearsOfExperience ?? "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Post Start Date">
                    {formatLongDateTime(jobOpportunity?.postStartDate)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Post End Date">
                    {formatLongDateTime(jobOpportunity?.postEndDate)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Required Skills">
                    {Array.isArray(jobOpportunity?.requiredSkills) && jobOpportunity.requiredSkills.length
                      ? jobOpportunity.requiredSkills.map((item) => item?.name || item?.code).join(", ")
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Other Required Skills">
                    {Array.isArray(jobOpportunity?.otherRequiredSkills) && jobOpportunity.otherRequiredSkills.length
                      ? jobOpportunity.otherRequiredSkills.join(", ")
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Required Specializations">
                    {Array.isArray(jobOpportunity?.requiredSpecializations) && jobOpportunity.requiredSpecializations.length
                      ? jobOpportunity.requiredSpecializations.map((item) => item?.name || item?.code).join(", ")
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Other Specializations">
                    {Array.isArray(jobOpportunity?.otherRequiredSpecializations) && jobOpportunity.otherRequiredSpecializations.length
                      ? jobOpportunity.otherRequiredSpecializations.join(", ")
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Work Areas">
                    {Array.isArray(jobOpportunity?.otherRequiredWorkAreas) && jobOpportunity.otherRequiredWorkAreas.length
                      ? jobOpportunity.otherRequiredWorkAreas.join(", ")
                      : "-"}
                  </Descriptions.Item>
                </Descriptions>

                <Descriptions bordered size="small" column={1}>
                  <Descriptions.Item label="Description">
                    <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}>
                      {jobOpportunity?.description || "-"}
                    </Typography.Paragraph>
                  </Descriptions.Item>
u                  <Descriptions.Item label="Locations">
                    <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}>
                      {formatLocations(jobOpportunity?.requiredLocations) || "-"}
                    </Typography.Paragraph>
                  </Descriptions.Item>
                  <Descriptions.Item label="Created At">
                    {formatLongDateTime(jobOpportunity?.createdAt)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Updated At">
                    {formatLongDateTime(jobOpportunity?.updatedAt)}
                  </Descriptions.Item>
                </Descriptions>
              </Space>
            ),
          },
          {
            key: "questions",
            label: "Questions",
            children: (
              <div>
                <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 8 }}>
                  <Typography.Text type="secondary">
                    Questions for this job opportunity only.
                  </Typography.Text>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setEditingQuestion(null);
                      setQuestionDrawerOpen(true);
                    }}
                  >
                    New Question
                  </Button>
                </div>

                {questionsError ? (
                  <Alert
                    type="error"
                    showIcon
                    message="Failed to load questions"
                    description={questionsError}
                    style={{ marginBottom: 12 }}
                    action={<Button size="small" onClick={loadQuestions}>Retry</Button>}
                  />
                ) : null}

                <Table
                  rowKey="id"
                  loading={questionsLoading}
                  columns={questionColumns}
                  dataSource={questions}
                  className="custom-table"
                  bordered
                  tableLayout="fixed"
                  scroll={{ x: "max-content" }}
                  size="small"
                  locale={{ emptyText: "No questions found." }}
                  onRow={(record) => ({
                    onClick: (e) => {
                      if (e?.target?.closest?.(".prof-jobs-actions")) return;
                      setSelectedQuestion(record);
                      setQuestionDetailsOpen(true);
                    },
                  })}
                />
              </div>
            ),
          },
        ]}
      />

      <JobOpportunityDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleSubmitJob}
        loading={saving}
        initialValues={mapOpportunityForForm(jobOpportunity)}
        skills={options.skills}
        specializations={options.specializations}
      />

      <JobOpportunityQuestionDrawer
        open={questionDrawerOpen}
        onClose={() => setQuestionDrawerOpen(false)}
        onSubmit={handleSubmitQuestion}
        loading={savingQuestion}
        initialValues={editingQuestion}
      />

      <JobOpportunityQuestionDetailsDrawer
        open={questionDetailsOpen}
        onClose={() => setQuestionDetailsOpen(false)}
        question={selectedQuestion}
        onEdit={(record) => {
          setQuestionDetailsOpen(false);
          setEditingQuestion(record);
          setQuestionDrawerOpen(true);
        }}
      />
    </div>
  );
}
